import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Alert,
    Button,
    Select,
    Spin,
    Tag,
    Typography,
} from "antd";

import {
    CalendarOutlined,
    CheckCircleOutlined,
    DownloadOutlined,
    FilePdfOutlined,
    ReloadOutlined,
    TeamOutlined,
} from "@ant-design/icons";

import { pdf, Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

import api from "../../../api/api";
import { useAcademicYear } from "../../../AcademicYearContext";

const { Text: AntText } = Typography;

/* ============================================================
   SCHOOL BRANDING
============================================================ */

const SCHOOL_NAME = "Salvation In Christ School System - SICSS";
const SCHOOL_SUBTITLE = "STUDENT ADMISSIONS & RECORDS";
const REPORT_TITLE = "STUDENT ADMISSION REGISTER";
const REPORT_FOOTER = "OFFICIAL STUDENT RECORD";

const COLORS = {
    navy: "#062A4B",
    deepBlue: "#063A6B",
    gold: "#F6C944",
    orange: "#C0750D",
    paper: "#FFFFFF",
    surface: "#F5F8FB",
    border: "#D5DFE8",
    borderLight: "#E8EDF2",
    text: "#172B3B",
    muted: "#647586",
    success: "#287A35",
    paleGold: "#FFF8E5",
    paleGreen: "#EFF7EA",
    tableHeader: "#E5E9EE",
    tableHeaderText: "#172B3B",
};

const PAGE_SIZE = 100;
const MAX_PAGES = 10000;

/* ============================================================
   SHARED HELPERS
============================================================ */

const getErrorMessage = (error, fallback) => {
    const data = error?.response?.data;

    if (typeof data?.detail === "string") return data.detail;
    if (typeof data?.message === "string") return data.message;

    if (data && typeof data === "object") {
        const firstValue = Object.values(data).flat()[0];

        if (typeof firstValue === "string") return firstValue;
    }

    return error?.message || fallback;
};

const getStudentName = (student) => {
    const fullName = String(student?.full_name || "").trim();

    if (fullName) return fullName;

    return [
        student?.first_name,
        student?.middle_name,
        student?.last_name,
    ]
        .filter(Boolean)
        .map((part) => String(part).trim())
        .filter(Boolean)
        .join(" ") || "N/A";
};

const normalizeGender = (gender) => {
    const value = String(gender || "").trim().toLowerCase();

    if (["m", "male"].includes(value)) return "Male";
    if (["f", "female"].includes(value)) return "Female";

    return gender ? String(gender).trim() : "N/A";
};

const getStudentStatus = (student) =>
    String(student?.status ?? student?.admission_status ?? "").trim() || "N/A";

const getGradeClassId = (student) => {
    const gradeClass = student?.grade_class;

    if (gradeClass && typeof gradeClass === "object") {
        return (
            gradeClass.id ??
            gradeClass.pk ??
            gradeClass.value ??
            student?.grade_class_id ??
            null
        );
    }

    return (
        student?.grade_class_id ??
        gradeClass ??
        null
    );
};

const getGradeClassName = (student) => {
    const gradeClass = student?.grade_class;

    return (
        student?.grade_class_name ||
        student?.grade_class_display ||
        (typeof gradeClass === "object"
            ? gradeClass?.name || gradeClass?.title
            : "") ||
        ""
    );
};

const extractPage = (data) => {
    if (Array.isArray(data)) {
        return { results: data, next: null, count: data.length };
    }

    const results =
        data?.results ??
        data?.students ??
        data?.data ??
        [];

    return {
        results: Array.isArray(results) ? results : [],
        next: data?.next ?? data?.pagination?.next ?? null,
        count: Number.isFinite(Number(data?.count))
            ? Number(data.count)
            : null,
    };
};

const sanitizeFileName = (value) =>
    String(value || "Student_Register")
        .trim()
        .replace(/[^a-z0-9_-]+/gi, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "") || "Student_Register";

const formatReportDate = () =>
    new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

const getInitials = (value) =>
    String(value || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase() || "SI";

/* ============================================================
   PDF STYLES
   - A4 landscape
   - First-page masthead and table heading
   - Student rows flow naturally onto later pages
   - Footer and page numbering remain fixed
============================================================ */

const pdfStyles = StyleSheet.create({
    page: {
        paddingTop: 27,
        paddingBottom: 42,
        paddingHorizontal: 30,
        backgroundColor: COLORS.paper,
        fontFamily: "Helvetica",
        fontSize: 9,
        color: COLORS.text,
    },

    masthead: {
        backgroundColor: COLORS.navy,
        paddingVertical: 14,
        paddingHorizontal: 17,
        borderBottomWidth: 4,
        borderBottomColor: COLORS.gold,
        marginBottom: 15,
    },

    mastheadTop: {
        flexDirection: "row",
        alignItems: "center",
    },

    brandMark: {
        width: 38,
        height: 38,
        borderWidth: 1.5,
        borderColor: COLORS.gold,
        borderRadius: 5,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: COLORS.deepBlue,
    },

    brandMarkText: {
        fontSize: 13,
        fontWeight: "bold",
        color: COLORS.gold,
    },

    brandIdentity: {
        flex: 1,
        marginLeft: 11,
    },

    schoolName: {
        fontSize: 17,
        fontWeight: "bold",
        color: COLORS.paper,
        marginBottom: 4,
    },

    schoolSubtitle: {
        fontSize: 8,
        color: COLORS.gold,
        letterSpacing: 1.1,
    },

    documentBadge: {
        paddingVertical: 6,
        paddingHorizontal: 8,
        borderWidth: 1,
        borderColor: COLORS.gold,
        borderRadius: 3,
    },

    documentBadgeText: {
        fontSize: 7,
        fontWeight: "bold",
        color: COLORS.gold,
    },

    mastheadRule: {
        height: 1,
        backgroundColor: COLORS.deepBlue,
        marginTop: 10,
        marginBottom: 8,
    },

    mastheadDescription: {
        fontSize: 8,
        color: "#F0EDEA",
        lineHeight: 1.5,
    },

    reportHeading: {
        marginBottom: 13,
    },

    reportEyebrow: {
        color: COLORS.orange,
        fontSize: 8,
        fontWeight: "bold",
        letterSpacing: 1,
        marginBottom: 5,
    },

    reportTitle: {
        color: COLORS.navy,
        fontSize: 16,
        fontWeight: "bold",
        marginBottom: 5,
    },

    reportDescription: {
        fontSize: 8.5,
        color: COLORS.muted,
        lineHeight: 1.5,
    },

    metadata: {
        flexDirection: "row",
        marginBottom: 13,
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.surface,
    },

    metadataItem: {
        flex: 1,
        paddingVertical: 8,
        paddingHorizontal: 9,
        borderRightWidth: 1,
        borderRightColor: COLORS.border,
    },

    metadataItemLast: {
        flex: 1,
        paddingVertical: 8,
        paddingHorizontal: 9,
    },

    metadataLabel: {
        fontSize: 7,
        color: COLORS.muted,
        fontWeight: "bold",
        marginBottom: 4,
    },

    metadataValue: {
        fontSize: 9,
        color: COLORS.navy,
        fontWeight: "bold",
        lineHeight: 1.4,
    },

    sectionHeading: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingVertical: 8,
        paddingHorizontal: 10,
        backgroundColor: COLORS.deepBlue,
        borderLeftWidth: 3,
        borderLeftColor: COLORS.gold,
    },

    sectionTitle: {
        fontSize: 9,
        fontWeight: "bold",
        color: COLORS.paper,
    },

    sectionSubtitle: {
        fontSize: 7.5,
        color: "#F0EDEA",
    },

    table: {
        width: "100%",
        borderWidth: 1,
        borderColor: COLORS.border,
        borderTopWidth: 0,
    },

    tableHeader: {
        flexDirection: "row",
        backgroundColor: COLORS.tableHeader,
        borderBottomWidth: 1,
        borderBottomColor: "#BBC5CF",
        minHeight: 27,
    },

    tableRow: {
        flexDirection: "row",
        minHeight: 26,
        backgroundColor: COLORS.paper,
        borderBottomWidth: 0.6,
        borderBottomColor: COLORS.borderLight,
    },

    tableRowAlternate: {
        flexDirection: "row",
        minHeight: 26,
        backgroundColor: "#F5F7F9",
        borderBottomWidth: 0.6,
        borderBottomColor: COLORS.borderLight,
    },

    cell: {
        paddingVertical: 6,
        paddingHorizontal: 7,
        fontSize: 8.5,
        color: COLORS.text,
        justifyContent: "center",
        lineHeight: 1.35,
    },

    headerCell: {
        paddingVertical: 8,
        paddingHorizontal: 7,
        fontSize: 7.5,
        fontWeight: "bold",
        color: COLORS.tableHeaderText,
        justifyContent: "center",
    },

    numberCell: {
        width: "7%",
        textAlign: "center",
        borderRightWidth: 0.5,
        borderRightColor: COLORS.border,
    },

    nameCell: { width: "37%" },
    genderCell: { width: "13%", textAlign: "center" },
    classCell: { width: "25%" },
    statusCell: { width: "18%", textAlign: "center" },

    summarySection: {
        marginTop: 15,
        marginBottom: 10,
    },

    summaryHeading: {
        fontSize: 9,
        fontWeight: "bold",
        color: COLORS.navy,
        marginBottom: 7,
    },

    summaryGrid: {
        flexDirection: "row",
    },

    summaryCard: {
        flex: 1,
        paddingVertical: 9,
        paddingHorizontal: 8,
        marginRight: 6,
        backgroundColor: COLORS.surface,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderTopWidth: 3,
        borderTopColor: COLORS.deepBlue,
    },

    summaryCardGold: {
        flex: 1,
        paddingVertical: 9,
        paddingHorizontal: 8,
        marginRight: 6,
        backgroundColor: COLORS.paleGold,
        borderWidth: 1,
        borderColor: "#F0DEAA",
        borderTopWidth: 3,
        borderTopColor: "#EFA81C",
    },

    summaryCardGreen: {
        flex: 1,
        paddingVertical: 9,
        paddingHorizontal: 8,
        backgroundColor: COLORS.paleGreen,
        borderWidth: 1,
        borderColor: "#D5E7CD",
        borderTopWidth: 3,
        borderTopColor: "#4FAE22",
    },

    summaryLabel: {
        color: COLORS.muted,
        fontSize: 7,
        fontWeight: "bold",
        marginBottom: 5,
    },

    summaryValue: {
        color: COLORS.navy,
        fontSize: 16,
        fontWeight: "bold",
    },

    summaryValueGold: {
        color: COLORS.orange,
        fontSize: 16,
        fontWeight: "bold",
    },

    summaryValueGreen: {
        color: COLORS.success,
        fontSize: 16,
        fontWeight: "bold",
    },

    summaryNote: {
        fontSize: 6.5,
        color: COLORS.muted,
        marginTop: 4,
    },

    documentNote: {
        padding: 9,
        backgroundColor: COLORS.surface,
        borderLeftWidth: 3,
        borderLeftColor: "#CBA454",
        marginTop: 5,
    },

    documentNoteTitle: {
        fontSize: 7.5,
        fontWeight: "bold",
        color: COLORS.navy,
        marginBottom: 4,
    },

    documentNoteText: {
        fontSize: 7.5,
        color: COLORS.muted,
        lineHeight: 1.5,
    },

    footer: {
        position: "absolute",
        bottom: 17,
        left: 30,
        right: 30,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    footerLeft: {
        flex: 1,
        fontSize: 7,
        color: COLORS.navy,
        fontWeight: "bold",
    },

    footerRight: {
        fontSize: 7,
        color: COLORS.muted,
        textAlign: "right",
    },

    emptyState: {
        padding: 20,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.surface,
    },

    emptyStateTitle: {
        fontSize: 11,
        fontWeight: "bold",
        color: COLORS.navy,
        marginBottom: 6,
    },

    emptyStateText: {
        fontSize: 8,
        color: COLORS.muted,
        textAlign: "center",
    },
});

/* ============================================================
   PDF DOCUMENT
============================================================ */

const StudentGradeClassPDF = ({
    students = [],
    gradeClassName,
    academicYearName,
    generatedDate,
}) => {
    const safeStudents = Array.isArray(students) ? students : [];

    const maleCount = safeStudents.filter(
        (student) => normalizeGender(student?.gender) === "Male"
    ).length;

    const femaleCount = safeStudents.filter(
        (student) => normalizeGender(student?.gender) === "Female"
    ).length;

    const unknownGenderCount =
        safeStudents.length - maleCount - femaleCount;

    return (
        <Document
            title={`${gradeClassName || "Grade Class"} - Student Admission Register`}
            author={SCHOOL_NAME}
            subject="Official Student Admission Register"
            creator="Student Records Management System"
            keywords={`Student Register, Admissions, ${academicYearName || ""}`}
        >
            <Page size="A4" orientation="landscape" style={pdfStyles.page} wrap>
                {/* This masthead intentionally appears on the first page only. */}
                <View style={pdfStyles.masthead}>
                    <View style={pdfStyles.mastheadTop}>
                        <View style={pdfStyles.brandMark}>
                            <Text style={pdfStyles.brandMarkText}>
                                {getInitials(SCHOOL_NAME)}
                            </Text>
                        </View>

                        <View style={pdfStyles.brandIdentity}>
                            <Text style={pdfStyles.schoolName}>
                                {SCHOOL_NAME}
                            </Text>
                            <Text style={pdfStyles.schoolSubtitle}>
                                {SCHOOL_SUBTITLE}
                            </Text>
                        </View>

                        <View style={pdfStyles.documentBadge}>
                            <Text style={pdfStyles.documentBadgeText}>
                                OFFICIAL RECORD
                            </Text>
                        </View>
                    </View>

                    <View style={pdfStyles.mastheadRule} />

                    <Text style={pdfStyles.mastheadDescription}>
                        Institutional student records and academic administration report.
                    </Text>
                </View>

                <View style={pdfStyles.reportHeading}>
                    <Text style={pdfStyles.reportEyebrow}>
                        ADMISSIONS / STUDENT RECORDS
                    </Text>

                    <Text style={pdfStyles.reportTitle}>
                        {REPORT_TITLE}
                    </Text>

                    <Text style={pdfStyles.reportDescription}>
                        Official student listing for the selected Grade Class and
                        academic year. This report contains {safeStudents.length}
                        {" "}student record{safeStudents.length === 1 ? "" : "s"}.
                    </Text>
                </View>

                <View style={pdfStyles.metadata}>
                    <View style={pdfStyles.metadataItem}>
                        <Text style={pdfStyles.metadataLabel}>GRADE CLASS</Text>
                        <Text style={pdfStyles.metadataValue}>
                            {gradeClassName || "Not specified"}
                        </Text>
                    </View>

                    <View style={pdfStyles.metadataItem}>
                        <Text style={pdfStyles.metadataLabel}>ACADEMIC YEAR</Text>
                        <Text style={pdfStyles.metadataValue}>
                            {academicYearName || "Not specified"}
                        </Text>
                    </View>

                    <View style={pdfStyles.metadataItem}>
                        <Text style={pdfStyles.metadataLabel}>REPORT DATE</Text>
                        <Text style={pdfStyles.metadataValue}>
                            {generatedDate || formatReportDate()}
                        </Text>
                    </View>

                    <View style={pdfStyles.metadataItemLast}>
                        <Text style={pdfStyles.metadataLabel}>TOTAL RECORDS</Text>
                        <Text style={pdfStyles.metadataValue}>
                            {safeStudents.length}
                        </Text>
                    </View>
                </View>

                <View style={pdfStyles.sectionHeading}>
                    <Text style={pdfStyles.sectionTitle}>STUDENT REGISTER</Text>
                    <Text style={pdfStyles.sectionSubtitle}>
                        {gradeClassName || "Selected Grade Class"}
                    </Text>
                </View>

                <View style={pdfStyles.table}>
                    {/* Table headings are not fixed and do not repeat. */}
                    <View style={pdfStyles.tableHeader}>
                        <Text style={[pdfStyles.headerCell, pdfStyles.numberCell]}>
                            NO.
                        </Text>
                        <Text style={[pdfStyles.headerCell, pdfStyles.nameCell]}>
                            STUDENT FULL NAME
                        </Text>
                        <Text style={[pdfStyles.headerCell, pdfStyles.genderCell]}>
                            GENDER
                        </Text>
                        <Text style={[pdfStyles.headerCell, pdfStyles.classCell]}>
                            GRADE CLASS
                        </Text>
                        <Text style={[pdfStyles.headerCell, pdfStyles.statusCell]}>
                            STATUS
                        </Text>
                    </View>

                    {safeStudents.length > 0 ? (
                        safeStudents.map((student, index) => (
                            <View
                                key={student.id ?? `student-${index}`}
                                style={
                                    index % 2 === 0
                                        ? pdfStyles.tableRow
                                        : pdfStyles.tableRowAlternate
                                }
                                wrap={false}
                            >
                                <Text style={[pdfStyles.cell, pdfStyles.numberCell]}>
                                    {index + 1}
                                </Text>

                                <Text style={[pdfStyles.cell, pdfStyles.nameCell]}>
                                    {getStudentName(student)}
                                </Text>

                                <Text style={[pdfStyles.cell, pdfStyles.genderCell]}>
                                    {normalizeGender(student.gender)}
                                </Text>

                                <Text style={[pdfStyles.cell, pdfStyles.classCell]}>
                                    {getGradeClassName(student) ||
                                        gradeClassName ||
                                        "N/A"}
                                </Text>

                                <Text style={[pdfStyles.cell, pdfStyles.statusCell]}>
                                    {getStudentStatus(student)}
                                </Text>
                            </View>
                        ))
                    ) : (
                        <View style={pdfStyles.emptyState}>
                            <Text style={pdfStyles.emptyStateTitle}>
                                No Student Records
                            </Text>
                            <Text style={pdfStyles.emptyStateText}>
                                No student records were available for this Grade Class.
                            </Text>
                        </View>
                    )}
                </View>

                <View style={pdfStyles.summarySection} wrap={false}>
                    <Text style={pdfStyles.summaryHeading}>REGISTER SUMMARY</Text>

                    <View style={pdfStyles.summaryGrid}>
                        <View style={pdfStyles.summaryCard}>
                            <Text style={pdfStyles.summaryLabel}>TOTAL STUDENTS</Text>
                            <Text style={pdfStyles.summaryValue}>
                                {safeStudents.length}
                            </Text>
                            <Text style={pdfStyles.summaryNote}>
                                Complete class register
                            </Text>
                        </View>

                        <View style={pdfStyles.summaryCardGold}>
                            <Text style={pdfStyles.summaryLabel}>MALE STUDENTS</Text>
                            <Text style={pdfStyles.summaryValueGold}>
                                {maleCount}
                            </Text>
                            <Text style={pdfStyles.summaryNote}>
                                Records identified as male
                            </Text>
                        </View>

                        <View style={pdfStyles.summaryCardGreen}>
                            <Text style={pdfStyles.summaryLabel}>FEMALE STUDENTS</Text>
                            <Text style={pdfStyles.summaryValueGreen}>
                                {femaleCount}
                            </Text>
                            <Text style={pdfStyles.summaryNote}>
                                Records identified as female
                            </Text>
                        </View>

                        <View style={pdfStyles.summaryCard}>
                            <Text style={pdfStyles.summaryLabel}>UNSPECIFIED GENDER</Text>
                            <Text style={pdfStyles.summaryValue}>
                                {unknownGenderCount}
                            </Text>
                            <Text style={pdfStyles.summaryNote}>
                                Unrecognized or unspecified value
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={pdfStyles.documentNote} wrap={false}>
                    <Text style={pdfStyles.documentNoteTitle}>
                        RECORDS VERIFICATION
                    </Text>
                    <Text style={pdfStyles.documentNoteText}>
                        This document is generated from student records available
                        in the school management system for the selected academic
                        year. Verify student details against official admission
                        records when necessary.
                    </Text>
                </View>

                {/* Only the footer repeats on subsequent pages. */}
                <View style={pdfStyles.footer} fixed>
                    <Text style={pdfStyles.footerLeft}>
                        {SCHOOL_NAME} | {REPORT_FOOTER}
                    </Text>

                    <Text
                        style={pdfStyles.footerRight}
                        render={({ pageNumber, totalPages }) =>
                            `Page ${pageNumber} of ${totalPages}`
                        }
                    />
                </View>
            </Page>
        </Document>
    );
};

/* ============================================================
   MAIN COMPONENT
============================================================ */

const AdmissionStuListByClass = () => {
    const { selectedYear } = useAcademicYear();

    const academicYearId = selectedYear?.id ?? null;
    const academicYearName = selectedYear?.name || "Not selected";

    const [gradeClasses, setGradeClasses] = useState([]);
    const [selectedGradeClassId, setSelectedGradeClassId] = useState(null);
    const [studentsInSelectedGrade, setStudentsInSelectedGrade] = useState([]);

    const [gradeClassesLoading, setGradeClassesLoading] = useState(false);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);

    // Separate request sequences prevent older responses replacing newer selections.
    const gradeClassRequestId = useRef(0);
    const studentRequestId = useRef(0);

    const getToken = useCallback(() => localStorage.getItem("access"), []);

    const handleUnauthorized = useCallback(() => {
        localStorage.removeItem("access");
        window.location.assign("/login");
    }, []);

    /* ========================================================
       LOAD GRADE CLASSES DIRECTLY FROM THE DEDICATED ENDPOINT

       This is the important fix: do not build dropdown options
       from the first page or partial results of the students API.
    ======================================================== */

    const fetchGradeClasses = useCallback(async () => {
        const requestId = ++gradeClassRequestId.current;
        const token = getToken();

        setSelectedGradeClassId(null);
        setStudentsInSelectedGrade([]);
        setGradeClasses([]);
        setError("");

        if (!token) {
            handleUnauthorized();
            return;
        }

        if (!academicYearId) {
            setGradeClassesLoading(false);
            setError("Select an academic year to load Grade Classes.");
            return;
        }

        setGradeClassesLoading(true);

        try {
            const response = await api.get(
                "/api/users/students/grade-classes-with-admissions/",
                {
                    params: { academic_year: academicYearId },
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (requestId !== gradeClassRequestId.current) return;

            const payload = response.data;
            const items = Array.isArray(payload)
                ? payload
                : payload?.results ?? payload?.grade_classes ?? payload?.data ?? [];

            if (!Array.isArray(items)) {
                throw new Error(
                    "The Grade Class endpoint returned an unsupported response format."
                );
            }

            const uniqueClasses = new Map();

            items.forEach((item) => {
                const id = item?.id ?? item?.grade_class_id;
                const name =
                    item?.name ??
                    item?.grade_class__name ??
                    item?.grade_class_name ??
                    item?.grade_class?.name;

                if (id === null || id === undefined || String(id).trim() === "") {
                    return;
                }

                const key = String(id);

                uniqueClasses.set(key, {
                    value: key,
                    label: String(name || `Grade Class ${key}`),
                    searchText: `${name || ""} ${key}`.toLowerCase(),
                    studentCount: Number(item?.student_count ?? item?.count ?? 0),
                });
            });

            const options = Array.from(uniqueClasses.values()).sort((a, b) =>
                a.label.localeCompare(b.label, undefined, {
                    numeric: true,
                    sensitivity: "base",
                })
            );

            setGradeClasses(options);

            if (options.length === 0) {
                setError(
                    "No Grade Classes with admission records were returned for this academic year. Confirm the selected year and backend endpoint."
                );
            }
        } catch (err) {
            if (requestId !== gradeClassRequestId.current) return;

            console.error("Unable to load Grade Classes:", err);

            if (err?.response?.status === 401) {
                handleUnauthorized();
                return;
            }

            if (err?.response?.status === 404) {
                setError(
                    "The Grade Class endpoint was not found. Register the grade-classes-with-admissions action on the StudentAdmissionView router."
                );
            } else {
                setError(
                    getErrorMessage(
                        err,
                        "Unable to load Grade Classes. Please refresh and try again."
                    )
                );
            }
        } finally {
            if (requestId === gradeClassRequestId.current) {
                setGradeClassesLoading(false);
            }
        }
    }, [academicYearId, getToken, handleUnauthorized]);

    useEffect(() => {
        fetchGradeClasses();

        return () => {
            gradeClassRequestId.current += 1;
        };
    }, [fetchGradeClasses, refreshKey]);

    /* ========================================================
       LOAD ALL STUDENTS FOR THE SELECTED GRADE CLASS

       Fetches each DRF page and deduplicates by student ID.
       The PDF therefore uses the complete class result, not
       just the first 100 records.
    ======================================================== */

    const fetchStudentsForGradeClass = useCallback(
        async (gradeClassId) => {
            const requestId = ++studentRequestId.current;
            const token = getToken();

            setStudentsInSelectedGrade([]);
            setError("");

            if (!token) {
                handleUnauthorized();
                return;
            }

            if (!academicYearId || gradeClassId === null || gradeClassId === undefined) {
                setStudentsLoading(false);
                return;
            }

            setStudentsLoading(true);

            try {
                let nextUrl = "/api/users/students/";
                let params = {
                    academic_year: academicYearId,
                    grade_class: gradeClassId,
                    page_size: PAGE_SIZE,
                    ordering: "full_name",
                };

                const allStudents = [];
                const seenIds = new Set();
                const visitedPages = new Set();
                let pageCount = 0;

                while (nextUrl) {
                    if (requestId !== studentRequestId.current) return;

                    const pageKey =
                        `${nextUrl}?${new URLSearchParams(
                            Object.entries(params).reduce(
                                (acc, [key, value]) => ({
                                    ...acc,
                                    [key]: String(value),
                                }),
                                {}
                            )
                        ).toString()}`;

                    if (visitedPages.has(pageKey)) {
                        throw new Error(
                            "Repeated pagination detected. The request was stopped to prevent an infinite loop."
                        );
                    }

                    visitedPages.add(pageKey);
                    pageCount += 1;

                    if (pageCount > MAX_PAGES) {
                        throw new Error(
                            "The class result exceeds the supported pagination limit."
                        );
                    }

                    const response = await api.get(nextUrl, {
                        params,
                        headers: { Authorization: `Bearer ${token}` },
                    });

                    if (requestId !== studentRequestId.current) return;

                    const page = extractPage(response.data);

                    page.results.forEach((student) => {
                        if (
                            !student ||
                            student.id === null ||
                            student.id === undefined
                        ) {
                            return;
                        }

                        const id = String(student.id);

                        if (!seenIds.has(id)) {
                            seenIds.add(id);
                            allStudents.push(student);
                        }
                    });

                    nextUrl = page.next || null;

                    // DRF's `next` URL already contains its query parameters.
                    // Do not re-apply the first-page params on subsequent pages.
                    params = {};
                }

                if (requestId !== studentRequestId.current) return;

                const sortedStudents = allStudents.sort((a, b) =>
                    getStudentName(a).localeCompare(
                        getStudentName(b),
                        undefined,
                        { sensitivity: "base" }
                    )
                );

                setStudentsInSelectedGrade(sortedStudents);

                if (sortedStudents.length === 0) {
                    setError(
                        "No student records were found for this Grade Class in the selected academic year."
                    );
                }
            } catch (err) {
                if (requestId !== studentRequestId.current) return;

                console.error("Unable to load class student records:", err);

                if (err?.response?.status === 401) {
                    handleUnauthorized();
                    return;
                }

                setStudentsInSelectedGrade([]);
                setError(
                    getErrorMessage(
                        err,
                        "Unable to retrieve all student records for this Grade Class."
                    )
                );
            } finally {
                if (requestId === studentRequestId.current) {
                    setStudentsLoading(false);
                }
            }
        },
        [academicYearId, getToken, handleUnauthorized]
    );

    useEffect(() => {
        if (selectedGradeClassId === null || selectedGradeClassId === undefined) {
            setStudentsInSelectedGrade([]);
            setStudentsLoading(false);
            return undefined;
        }

        fetchStudentsForGradeClass(selectedGradeClassId);

        return () => {
            studentRequestId.current += 1;
        };
    }, [selectedGradeClassId, fetchStudentsForGradeClass, refreshKey]);

    /* ========================================================
       SELECTED CLASS AND COUNTS
    ======================================================== */

    const selectedGradeClass = useMemo(
        () =>
            gradeClasses.find(
                (item) => String(item.value) === String(selectedGradeClassId)
            ) || null,
        [gradeClasses, selectedGradeClassId]
    );

    const maleCount = useMemo(
        () =>
            studentsInSelectedGrade.filter(
                (student) => normalizeGender(student?.gender) === "Male"
            ).length,
        [studentsInSelectedGrade]
    );

    const femaleCount = useMemo(
        () =>
            studentsInSelectedGrade.filter(
                (student) => normalizeGender(student?.gender) === "Female"
            ).length,
        [studentsInSelectedGrade]
    );

    const handleGradeClassChange = (value) => {
        // Invalidate any student fetch associated with the previous selection.
        studentRequestId.current += 1;
        setError("");
        setStudentsInSelectedGrade([]);
        setSelectedGradeClassId(value ?? null);
    };

    const handleRefresh = () => {
        // Changing the key refreshes the Grade Class list and current class data.
        setError("");
        setRefreshKey((value) => value + 1);
    };

    /* ========================================================
       DOWNLOAD COMPLETE CLASS PDF
    ======================================================== */

    const handleDownloadPDF = async () => {
        if (!academicYearId) {
            setError("Select an academic year before generating the report.");
            return;
        }

        if (selectedGradeClassId === null || selectedGradeClassId === undefined) {
            setError("Please select a Grade Class first.");
            return;
        }

        if (studentsLoading) {
            setError("Please wait until the class student records finish loading.");
            return;
        }

        if (studentsInSelectedGrade.length === 0) {
            setError("There are no student records available to include in this PDF.");
            return;
        }

        try {
            setDownloading(true);
            setError("");

            const className = selectedGradeClass?.label || "Grade Class";

            const blob = await pdf(
                <StudentGradeClassPDF
                    students={[...studentsInSelectedGrade]}
                    gradeClassName={className}
                    academicYearName={academicYearName}
                    generatedDate={formatReportDate()}
                />
            ).toBlob();

            if (!blob || blob.size === 0) {
                throw new Error("The generated PDF is empty.");
            }

            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = objectUrl;
            link.download =
                `${sanitizeFileName(SCHOOL_NAME)}_` +
                `${sanitizeFileName(className)}_Student_Register_` +
                `${sanitizeFileName(academicYearName)}.pdf`;

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
        } catch (err) {
            console.error("PDF generation error:", err);
            setError(
                getErrorMessage(
                    err,
                    "Unable to generate the PDF. Please try again."
                )
            );
        } finally {
            setDownloading(false);
        }
    };

    /* ========================================================
       USER INTERFACE
    ======================================================== */

    return (
        <div className="admission-grade-download">
            <div className="admission-grade-download-header">
                <div className="admission-grade-download-icon">
                    <FilePdfOutlined />
                </div>

                <div className="admission-grade-download-heading">
                    <span className="admission-grade-eyebrow">
                        ADMISSIONS / STUDENT RECORDS
                    </span>

                    <div className="admission-grade-title">
                        Student Admission Register
                    </div>

                    <span className="admission-grade-description">
                        Select a Grade Class to review its records and generate
                        a complete, print-ready PDF register.
                    </span>
                </div>

                <div className="admission-grade-year">
                    <CalendarOutlined />
                    <div>
                        <span>Academic Year</span>
                        <strong>{academicYearName}</strong>
                    </div>
                </div>
            </div>

            <div className="admission-grade-download-content">
                {error && (
                    <Alert
                        className="admission-grade-alert"
                        type="error"
                        showIcon
                        message="Unable to complete the request"
                        description={error}
                        closable
                        onClose={() => setError("")}
                    />
                )}

                {!academicYearId && (
                    <Alert
                        className="admission-grade-alert"
                        type="warning"
                        showIcon
                        message="Academic year required"
                        description="Select an academic year from the school management system before loading Grade Classes."
                    />
                )}

                <div className="admission-grade-selector">
                    <label htmlFor="admission-grade-class-select">
                        Select Grade Class
                    </label>

                    <Select
                        id="admission-grade-class-select"
                        showSearch
                        allowClear
                        size="large"
                        placeholder="Search or select a Grade Class"
                        value={selectedGradeClassId ?? undefined}
                        options={gradeClasses}
                        loading={gradeClassesLoading}
                        disabled={!academicYearId || gradeClassesLoading}
                        style={{ width: "100%" }}
                        optionFilterProp="searchText"
                        filterOption={(input, option) =>
                            String(option?.searchText ?? option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        onChange={handleGradeClassChange}
                        notFoundContent={
                            gradeClassesLoading ? (
                                <Spin size="small" />
                            ) : (
                                "No Grade Classes found"
                            )
                        }
                    />
                </div>

                <div className="admission-total-records">
                    <div className="admission-records-icon">
                        <TeamOutlined />
                    </div>

                    <div className="admission-records-copy">
                        <span>Grade Classes available</span>
                        <small>
                            {gradeClassesLoading
                                ? "Loading Grade Classes for the selected academic year..."
                                : `${gradeClasses.length} Grade Class${gradeClasses.length === 1 ? "" : "es"} returned by the server`}
                        </small>
                    </div>

                    <strong>{gradeClassesLoading ? "—" : gradeClasses.length}</strong>

                    <Button
                        type="text"
                        icon={<ReloadOutlined />}
                        onClick={handleRefresh}
                        loading={gradeClassesLoading || studentsLoading}
                        aria-label="Refresh Grade Classes and student records"
                        title="Refresh records"
                    />
                </div>

                {gradeClassesLoading && (
                    <div className="admission-grade-loading">
                        <Spin />
                        <span>Loading Grade Classes...</span>
                    </div>
                )}

                {selectedGradeClass && (
                    <>
                        <div className="admission-selected-class">
                            <div className="admission-selected-class-main">
                                <div className="admission-selected-class-icon">
                                    <TeamOutlined />
                                </div>

                                <div>
                                    <span>Selected Grade Class</span>
                                    <strong>{selectedGradeClass.label}</strong>
                                </div>
                            </div>

                            <div className="admission-selected-year">
                                <span>Academic Year</span>
                                <strong>{academicYearName}</strong>
                            </div>

                            <Tag color="green" className="admission-student-count">
                                <TeamOutlined />
                                {studentsLoading
                                    ? "Loading students..."
                                    : `${studentsInSelectedGrade.length} student${studentsInSelectedGrade.length === 1 ? "" : "s"}`}
                            </Tag>
                        </div>

                        {studentsLoading && (
                            <div className="admission-grade-loading">
                                <Spin />
                                <span>
                                    Loading all student records for{" "}
                                    {selectedGradeClass.label}...
                                </span>
                            </div>
                        )}

                        {!studentsLoading && studentsInSelectedGrade.length > 0 && (
                            <div className="admission-student-preview">
                                <div>
                                    <strong>Student records ready</strong>
                                    <span>
                                        {studentsInSelectedGrade.length} records
                                        loaded across all available pages.
                                    </span>
                                </div>

                                <div className="admission-preview-counts">
                                    <Tag color="blue">
                                        Male: {maleCount}
                                    </Tag>
                                    <Tag color="magenta">
                                        Female: {femaleCount}
                                    </Tag>
                                </div>
                            </div>
                        )}

                        <div className="admission-download-action">
                            <Button
                                type="primary"
                                size="large"
                                icon={<DownloadOutlined />}
                                loading={downloading}
                                disabled={
                                    studentsLoading ||
                                    !selectedGradeClassId ||
                                    studentsInSelectedGrade.length === 0
                                }
                                onClick={handleDownloadPDF}
                            >
                                {downloading
                                    ? "Generating PDF..."
                                    : "Download Complete Student Register"}
                            </Button>

                            {!studentsLoading &&
                                studentsInSelectedGrade.length > 0 && (
                                    <span className="admission-download-ready">
                                        <CheckCircleOutlined />
                                        All available class records are ready
                                    </span>
                                )}
                        </div>
                    </>
                )}
            </div>

            <style>{`
                .admission-grade-download {
                    --navy: #062A4B;
                    --deep-blue: #063A6B;
                    --gold: #F6C944;
                    --golden-yellow: #EFA81C;
                    --orange: #C0750D;
                    --sky-blue: #0F6DAB;

                    overflow: hidden;
                    border-radius: 14px;
                    background: #FFFFFF;
                    border: 1px solid #D5DFE8;
                    box-shadow: 0 8px 30px rgba(6, 42, 75, 0.07);
                    color: #0A0F12;
                }

                .admission-grade-download-header {
                    min-height: 100px;
                    padding: 20px 23px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    background: linear-gradient(
                        115deg,
                        rgba(6, 42, 75, 0.04),
                        #FFFFFF 57%,
                        rgba(246, 201, 68, 0.11)
                    );
                    border-bottom: 1px solid #D5DFE8;
                    border-top: 4px solid var(--navy);
                }

                .admission-grade-download-icon {
                    width: 49px;
                    height: 49px;
                    min-width: 49px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 12px;
                    background: var(--navy);
                    color: var(--gold);
                    border: 1px solid var(--deep-blue);
                    font-size: 22px;
                }

                .admission-grade-download-heading {
                    min-width: 0;
                    flex: 1;
                }

                .admission-grade-eyebrow {
                    display: block;
                    color: var(--orange);
                    font-size: 9px;
                    line-height: 1.3;
                    font-weight: 800;
                    letter-spacing: 1px;
                    margin-bottom: 5px;
                }

                .admission-grade-title {
                    color: var(--navy);
                    font-size: 19px;
                    line-height: 1.3;
                    font-weight: 800;
                    letter-spacing: -0.35px;
                }

                .admission-grade-description {
                    display: block;
                    margin-top: 5px;
                    color: #647586;
                    font-size: 11px;
                    line-height: 1.6;
                }

                .admission-grade-year {
                    min-width: 158px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 11px 13px;
                    border-radius: 9px;
                    background: #FFFFFF;
                    color: var(--deep-blue);
                    border: 1px solid #D5DFE8;
                }

                .admission-grade-year span,
                .admission-selected-year span {
                    display: block;
                    color: #647586;
                    font-size: 8px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: .7px;
                    margin-bottom: 4px;
                }

                .admission-grade-year strong,
                .admission-selected-year strong {
                    display: block;
                    color: var(--navy);
                    font-size: 12px;
                    overflow-wrap: anywhere;
                }

                .admission-grade-download-content {
                    padding: 21px 23px 23px;
                }

                .admission-grade-alert {
                    margin-bottom: 15px;
                    border-radius: 9px;
                }

                .admission-grade-selector label {
                    display: block;
                    margin-bottom: 8px;
                    color: var(--navy);
                    font-size: 12px;
                    font-weight: 800;
                }

                .admission-grade-selector .ant-select-selector {
                    border-radius: 9px !important;
                    min-height: 45px !important;
                    border-color: #CBD7E1 !important;
                    box-shadow: none !important;
                }

                .admission-grade-selector .ant-select-selection-item,
                .admission-grade-selector .ant-select-selection-placeholder {
                    line-height: 43px !important;
                }

                .admission-grade-selector .ant-select-focused .ant-select-selector {
                    border-color: var(--sky-blue) !important;
                    box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.09) !important;
                }

                .admission-total-records {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    margin-top: 14px;
                    padding: 12px 14px;
                    border-radius: 10px;
                    background: #F4F7FA;
                    border: 1px solid #DCE5ED;
                }

                .admission-records-icon {
                    width: 35px;
                    height: 35px;
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 9px;
                    background: var(--navy);
                    color: var(--gold);
                    font-size: 16px;
                }

                .admission-records-copy {
                    flex: 1;
                    min-width: 0;
                }

                .admission-records-copy span {
                    display: block;
                    color: var(--navy);
                    font-size: 11px;
                    font-weight: 800;
                }

                .admission-records-copy small {
                    display: block;
                    margin-top: 3px;
                    color: #647586;
                    font-size: 10px;
                    line-height: 1.5;
                }

                .admission-total-records > strong {
                    color: var(--deep-blue);
                    font-size: 19px;
                    font-weight: 800;
                    font-variant-numeric: tabular-nums;
                }

                .admission-selected-class {
                    margin-top: 15px;
                    padding: 14px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex-wrap: wrap;
                    border-radius: 11px;
                    background: #F5F8FB;
                    border: 1px solid #D5DFE8;
                    border-left: 4px solid var(--golden-yellow);
                }

                .admission-selected-class-main {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    flex: 1;
                    min-width: 190px;
                }

                .admission-selected-class-icon {
                    width: 39px;
                    height: 39px;
                    min-width: 39px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 10px;
                    background: var(--navy);
                    color: var(--gold);
                    font-size: 17px;
                }

                .admission-selected-class-main span {
                    display: block;
                    color: #647586;
                    font-size: 8px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: .65px;
                    margin-bottom: 4px;
                }

                .admission-selected-class-main strong {
                    display: block;
                    color: var(--navy);
                    font-size: 14px;
                    overflow-wrap: anywhere;
                }

                .admission-student-count {
                    margin: 0;
                    min-height: 33px;
                    padding: 5px 10px;
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    border: 1px solid #D5E7CD;
                    border-radius: 8px;
                    background: #EFF7EA;
                    color: #287A35;
                    font-weight: 800;
                }

                .admission-student-preview {
                    margin-top: 13px;
                    padding: 13px 14px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                    border: 1px solid #D5E7CD;
                    border-radius: 9px;
                    background: #F7FBF4;
                }

                .admission-student-preview strong,
                .admission-student-preview span {
                    display: block;
                }

                .admission-student-preview strong {
                    color: var(--navy);
                    font-size: 11px;
                }

                .admission-student-preview span {
                    margin-top: 4px;
                    color: #647586;
                    font-size: 10px;
                }

                .admission-preview-counts {
                    display: flex;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .admission-download-action {
                    margin-top: 17px;
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    flex-wrap: wrap;
                }

                .admission-download-action .ant-btn-primary {
                    min-height: 44px;
                    padding: 0 19px;
                    border-radius: 9px;
                    background: var(--navy);
                    border-color: var(--navy);
                    color: #FFFFFF;
                    font-weight: 800;
                    box-shadow: 0 5px 13px rgba(6, 42, 75, 0.13);
                    transition: all .2s ease;
                }

                .admission-download-action .ant-btn-primary:hover:not(:disabled) {
                    background: var(--deep-blue) !important;
                    border-color: var(--deep-blue) !important;
                    transform: translateY(-1px);
                }

                .admission-download-action .ant-btn-primary:disabled {
                    background: #DCE3E9 !important;
                    border-color: #DCE3E9 !important;
                    color: #788795 !important;
                    box-shadow: none;
                }

                .admission-download-ready {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: #287A35;
                    font-size: 10px;
                    font-weight: 700;
                }

                .admission-grade-loading {
                    min-height: 100px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    color: var(--navy);
                    font-size: 12px;
                }

                @media (max-width: 760px) {
                    .admission-grade-download-header {
                        align-items: flex-start;
                        flex-wrap: wrap;
                        padding: 17px;
                    }

                    .admission-grade-year {
                        width: 100%;
                        box-sizing: border-box;
                    }

                    .admission-grade-download-content {
                        padding: 17px;
                    }
                }

                @media (max-width: 520px) {
                    .admission-grade-download {
                        border-radius: 11px;
                    }

                    .admission-grade-download-header {
                        gap: 11px;
                    }

                    .admission-grade-download-icon {
                        width: 41px;
                        height: 41px;
                        min-width: 41px;
                        font-size: 19px;
                    }

                    .admission-grade-title {
                        font-size: 16px;
                    }

                    .admission-selected-class {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .admission-selected-class-main {
                        min-width: 0;
                        width: 100%;
                    }

                    .admission-download-action {
                        align-items: stretch;
                        flex-direction: column;
                    }

                    .admission-download-action .ant-btn {
                        width: 100%;
                    }

                    .admission-download-ready {
                        line-height: 1.5;
                    }
                }
            `}</style>
        </div>
    );
};

export default AdmissionStuListByClass;